import React, { useState, useEffect } from "react";
import { ClipLoader } from "react-spinners";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

import api from "../../../../../services/api";
import { unitFormat } from "../../../../../utils/unitFormat";
import { useTheme } from "../../../../../hooks/theme";
import { useCompanies } from "../../../../../hooks/companies";
import { useDateFilter } from "../../../../../hooks/datefilter";
import { paramsOcorrenciasAbertas } from "../ocorrenciasAbertasParams";

// PO1-3214: NFs não entregues cuja última ocorrência não é finalizadora, por tipo
// de ocorrência. Mesmo recorte do card "Ocorrências abertas" (IndicadoresEntrega):
// período global (padrão 90 dias) e transportadora selecionada.

export default function ChartOcorrenciasEmAberto() {
  const { darkMode } = useTheme();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const color = darkMode ? "#e9ecef" : "#1e1e2f";
  const { transportadoraId, arrayParams } = useCompanies();
  const { startDate, endDate } = useDateFilter();

  useEffect(() => {
    let isMounted = true;

    async function loadCharts() {
      if (isMounted) {
        setLoading(true);
      }
      try {
        const response = await api.get(
          "/dashboard/performance/ocorrencias-abertas",
          {
            params: paramsOcorrenciasAbertas({
              startDate,
              endDate,
              transportadoras: arrayParams.transportadoras,
            }),
          }
        );

        if (isMounted && response.status === 200) {
          setData(response.data);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Erro ao carregar ocorrências em aberto:", error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    loadCharts();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, transportadoraId]);

  const CustomTooltip = (props) => {
    const { active } = props;

    if (active) {
      const { payload } = props;
      const payFormatted = payload.map((pay) => ({
        ref: "descrição",
        descricao: pay.payload.descricao,
        name: pay.name,
        value: unitFormat(pay.value),
        mediaDias: pay.payload.media_dias,
      }));
      return (
        <div
          className="tooltip-inner"
          style={{
            background: darkMode ? "#e9ecef" : "#1e1e2f",
            maxWidth: "500px",
            textAlign: "left",
          }}
        >
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].ref}: `}</strong>
            {payFormatted[0].descricao}
          </p>
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>{`${payFormatted[0].name}: `}</strong>
            {payFormatted[0].value}
          </p>
          <p style={{ color: darkMode ? "#000" : "#fff", fontWeight: 100 }}>
            <strong>média de dias desde a ocorrência: </strong>
            {unitFormat(payFormatted[0].mediaDias)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card
      style={{ boxShadow: "0px 1px 20px 0px rgb(0, 0, 0)" }}
      className="card-chart"
    >
      <CardHeader>
        <h4>Ocorrências em aberto</h4>
        <CardTitle>NFs não entregues com ocorrência pendente, por ocorrência</CardTitle>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div style={{ textAlign: "center" }}>
            <ClipLoader
              sizeUnit="px"
              size={90}
              color="#1d8cf8"
              loading={loading}
            />
            <br />
            <br />
            <h1 className="text-info" style={{ textAlign: "center" }}>
              Carregando ...
            </h1>
          </div>
        ) : (
          <Row className="justify-content-center">
            <Col>
              <ResponsiveContainer width="100%" height={250}>
                {data.length > 0 ? (
                  <BarChart
                    data={data}
                    margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="bar2" x1="0" y1="0" x2="0" y2="1">
                        <stop
                          offset="20%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="90%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="90%"
                          stopColor="#1d8cf8"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      stroke="rgba(29,140,248, 0.1)"
                      strokeWidth={1}
                    />
                    <XAxis
                      dataKey="codigo"
                      stroke={color}
                      style={{ fontWeight: 100 }}
                    />
                    <YAxis
                      tickFormatter={(tick) => unitFormat(tick)}
                      stroke={color}
                      style={{ fontWeight: 100 }}
                      width={60}
                    />
                    <Tooltip content={(props) => CustomTooltip(props)} />
                    <Legend />
                    <Bar
                      name="NFs em aberto"
                      dataKey="qtd"
                      type="monotone"
                      stroke="#0096FF"
                      fillOpacity={1}
                      fill="url(#bar2)"
                    >
                      <LabelList dataKey="qtd" position="top" />
                    </Bar>
                  </BarChart>
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <h5>Nenhum dado para apresentar!</h5>
                  </div>
                )}
              </ResponsiveContainer>
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
}
